import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  MenuItem,
  IconButton,
  FormControl,
  FormControlLabel,
  Select,
  Chip,
  InputLabel,
  OutlinedInput,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Checkbox,
  Dialog,
  DialogTitle,
  Switch,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Snackbar,
  Alert,
  CircularProgress,
  Tooltip
} from '@mui/material';
import { Close as CloseIcon, CalendarToday as CalendarIcon, Visibility as VisibilityIcon, PictureAsPdf as PdfIcon, Edit as EditIcon, Delete as DeleteIcon, CheckCircle as CheckCircleIcon, Info as InfoIcon } from '@mui/icons-material';
import { collection, query, where, getDocs, addDoc, updateDoc, doc as firestoreDoc, getDoc, orderBy, serverTimestamp, deleteDoc, writeBatch } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { normalizeDateFormat, formatShortDate, getShortDayName, getDayName, generateDaysBetweenDates } from '../../utils/dateUtils';

// Fonctions utilitaires
const formatDate = (date) => {
  if (!date) return 'Date non définie';
  
  // Si c'est un timestamp Firestore (avec toDate)
  if (date && typeof date.toDate === 'function') {
    return date.toDate().toLocaleDateString('fr-FR');
  }
  
  // Si c'est déjà un objet Date
  if (date instanceof Date) {
    return date.toLocaleDateString('fr-FR');
  }
  
  // Sinon, essayer de convertir en Date
  try {
    return new Date(date).toLocaleDateString('fr-FR');
  } catch (error) {
    console.error('Erreur de formatage de date:', error);
    return 'Date invalide';
  }
};


const formatDayName = (date) => {
  return new Date(date).toLocaleDateString('fr-FR', { weekday: 'long' });
};



// Fonction pour générer les dates entre deux dates
const getDatesInRange = (startDate, endDate) => {
  const dates = [];
  const currentDate = new Date(startDate);
  const lastDate = new Date(endDate);
  
  while (currentDate <= lastDate) {
    dates.push(new Date(currentDate));
    currentDate.setDate(currentDate.getDate() + 1);
  }
  
  return dates;
};

// Fonction pour organiser les données pour le tableau
const organizeDataForTable = (permanences, personnel) => {
  // Récupérer toutes les dates uniques
  const allDates = new Set();
  permanences.forEach(p => p.days.forEach(d => allDates.add(d)));
  const sortedDates = Array.from(allDates).sort();

  // Récupérer tout le personnel (même ceux sans permanence)
  const personnelMap = new Map();
  
  // D'abord, ajouter tout le personnel disponible
  personnel.forEach(person => {
    personnelMap.set(person.id, {
      person,
      dates: new Array(sortedDates.length).fill(false)
    });
  });
  
  // Puis ajouter le personnel des permanences (au cas où il y aurait des IDs non trouvés)
  permanences.forEach(p => {
    // Adapter pour gérer à la fois les tableaux personnel et les propriétés personnelId
    const personIds = p.personnel || (p.personnelId ? [p.personnelId] : []);
    
    personIds.forEach(personId => {
      if (!personnelMap.has(personId)) {
        const person = personnel.find(p => p.id === personId);
        if (person) {
          personnelMap.set(personId, {
            person,
            dates: new Array(sortedDates.length).fill(false)
          });
        }
      }
    });
  });

  // Marquer les présences
  permanences.forEach(p => {
    const personIds = p.personnel || (p.personnelId ? [p.personnelId] : []);
    
    personIds.forEach(personId => {
      const personRow = personnelMap.get(personId);
      if (personRow) {
        p.days.forEach(day => {
          const dateIndex = sortedDates.indexOf(day);
          if (dateIndex !== -1) {
            personRow.dates[dateIndex] = true;
          }
        });
      }
    });
  });

  // Convertir en tableau et trier par ordre alphabétique (nom de famille puis prénom)
  const personnelArray = Array.from(personnelMap.values());
  personnelArray.sort((a, b) => {
    // D'abord comparer les noms de famille
    const lastNameA = (a.person.lastName || '').toLowerCase();
    const lastNameB = (b.person.lastName || '').toLowerCase();
    
    if (lastNameA < lastNameB) return -1;
    if (lastNameA > lastNameB) return 1;
    
    // Si les noms sont identiques, comparer les prénoms
    const firstNameA = (a.person.firstName || '').toLowerCase();
    const firstNameB = (b.person.firstName || '').toLowerCase();
    
    if (firstNameA < firstNameB) return -1;
    if (firstNameA > firstNameB) return 1;
    
    return 0;
  });

  return {
    dates: sortedDates,
    personnel: personnelArray
  };
};

// Composant pour le contenu PDF avec des tailles de police plus grandes pour une meilleure lisibilité
const PdfContent = React.forwardRef(({ periodName, permanences, personnel, anneeScolaire, responsableEncadrement, establishmentName, gestionnaire, principal, observations, logo }, ref) => {
  // Organiser les données une seule fois pour éviter les calculs répétés
  const organizedData = organizeDataForTable(permanences, personnel);
  
  // Utiliser les observations passées en paramètre ou les récupérer des permanences
  const observationsText = observations || (permanences && permanences.length > 0 ? permanences[0].observations || '' : '');

  // Fonction locale pour vérifier si une date est un jour férié
  const checkJourFerie = (date) => {
    const joursFeriesData = {
      '2025-01-01': { nom: 'Jour de l\'an', couleur: '#ffebee' },
      '2025-04-21': { nom: 'Lundi de Pâques', couleur: '#e8f5e9' },
      '2025-05-01': { nom: 'Fête du Travail', couleur: '#e3f2fd' },
      '2025-05-08': { nom: 'Victoire 1945', couleur: '#ede7f6' },
      '2025-05-29': { nom: 'Ascension', couleur: '#fff3e0' },
      '2025-06-09': { nom: 'Lundi de Pentecôte', couleur: '#fce4ec' },
      '2025-07-14': { nom: 'Fête Nationale', couleur: '#e8eaf6' },
      '2025-08-15': { nom: 'Assomption', couleur: '#f3e5f5' },
      '2025-11-01': { nom: 'Toussaint', couleur: '#fff8e1' },
      '2025-11-11': { nom: 'Armistice', couleur: '#e0f7fa' },
      '2025-12-25': { nom: 'Noël', couleur: '#f1f8e9' }
    };
    
    const dateStr = date.toISOString().split('T')[0];
    return joursFeriesData[dateStr] || null;
  };
  
  // Détecter les changements de mois pour ajouter une démarcation
  const monthBoundaries = [];
  if (organizedData.dates.length > 1) {
    for (let i = 1; i < organizedData.dates.length; i++) {
      const prevDate = new Date(organizedData.dates[i-1]);
      const currDate = new Date(organizedData.dates[i]);
      if (prevDate.getMonth() !== currDate.getMonth()) {
        monthBoundaries.push(i);
      }
    }
  }
  
  const dateCount = organizedData.dates.length;

  // Tailles de police adaptées au nombre de colonnes de dates
  // Colonnes fixes (Nom+Grade+Service) = ~50 % → dates ont ~50 % de 756px ≈ 378px
  const dateFontSize   = Math.max(5,  Math.min(10, Math.floor(378 / Math.max(1, dateCount * 2.8))));
  const bodyFontSize   = Math.max(7,  Math.min(12, dateFontSize * 1.2));
  const headerFontSize = Math.max(8,  Math.min(14, dateFontSize * 1.4));

  // Diviser le personnel en pages de 21 personnes maximum
  const personnelPerPage = 21;
  const personnelPages = [];
  for (let i = 0; i < organizedData.personnel.length; i += personnelPerPage) {
    personnelPages.push(organizedData.personnel.slice(i, i + personnelPerPage));
  }

  return (
    <div ref={ref} id="pdf-content-container" style={{ 
      width: '210mm', // Largeur A4
      margin: '0 auto',
      fontFamily: 'Arial, sans-serif',
      fontSize: '11px',
      boxSizing: 'border-box',
      backgroundColor: '#ffffff',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {personnelPages.map((personnelPage, pageIndex) => (
        <div key={`page-${pageIndex}`} className="pdf-page" style={{
          width: '210mm',
          height: '297mm',
          fontSize: '11px',
          padding: '5mm',
          margin: pageIndex > 0 ? '20px auto 0' : '0 auto',
          pageBreakAfter: 'always',
          boxSizing: 'border-box',
          fontFamily: 'Arial, sans-serif',
          backgroundColor: '#ffffff',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative'
        }} data-pdf-page={pageIndex + 1}>
          <div style={{ marginBottom: '5px', borderBottom: '2px solid #1976d2', paddingBottom: '3px', position: 'relative' }}>
            {/* Logo à gauche */}
            {logo && logo.startsWith('data:image/') && (
              <div style={{ position: 'absolute', left: 0, top: 0 }}>
                <img 
                  src={logo} 
                  alt="Logo de l'établissement" 
                  style={{ 
                    maxHeight: '60px', 
                    maxWidth: '100px', 
                    objectFit: 'contain'
                  }} 
                />
              </div>
            )}
            
            {/* Logo à droite */}
            {logo && logo.startsWith('data:image/') && (
              <div style={{ position: 'absolute', right: 0, top: 0 }}>
                <img 
                  src={logo} 
                  alt="Logo de l'établissement" 
                  style={{ 
                    maxHeight: '60px', 
                    maxWidth: '100px', 
                    objectFit: 'contain'
                  }} 
                />
              </div>
            )}
            <div style={{ textAlign: 'center', paddingTop: '40px' }}>
              <h1 style={{ fontSize: '16px', margin: '0 0 3px 0', fontWeight: 'bold', color: '#1976d2' }}>
                {establishmentName || 'Nom de l\'établissement'}
              </h1>
              <h2 style={{ fontSize: '14px', margin: '0 0 3px 0', fontWeight: 'bold' }}>
                Planning des permanences - {periodName}
              </h2>
              <h3 style={{ fontSize: '12px', margin: '0', fontWeight: 'normal' }}>
                Année scolaire: {anneeScolaire || '2024-2025'} - Page {pageIndex + 1}/{personnelPages.length}
              </h3>
            </div>
          </div>
          
          {/* Pied de page */}
          {/* L'ancien pied de page centré a été supprimé */}

          <div style={{ overflow: 'hidden', width: '100%', flex: '1 1 auto' }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              marginBottom: '8px',
              fontSize: `${bodyFontSize}px`,
              tableLayout: 'fixed'
            }}>
              <thead>
                <tr>
                  <th style={{ border: '1px solid #ddd', padding: '2px', backgroundColor: '#e6f2ff', fontSize: `${headerFontSize}px`, fontWeight: 'bold', width: '25%', overflow: 'hidden' }} rowSpan={2}>Nom et Prénom</th>
                  <th style={{ border: '1px solid #ddd', padding: '2px', backgroundColor: '#e6f2ff', fontSize: `${headerFontSize}px`, fontWeight: 'bold', width: '10%', overflow: 'hidden' }} rowSpan={2}>Grade</th>
                  <th style={{ border: '1px solid #ddd', padding: '2px', backgroundColor: '#e6f2ff', fontSize: `${headerFontSize}px`, fontWeight: 'bold', width: '15%', overflow: 'hidden' }} rowSpan={2}>Service</th>
                  {organizedData.dates.map((date, index) => {
                    const dateObj = new Date(date);
                    const isMonthBoundary = monthBoundaries.includes(index);
                    return (
                      <th
                        key={`day-${date}`}
                        style={{
                          border: '1px solid #ddd',
                          padding: '1px',
                          backgroundColor: '#f5f5f5',
                          textAlign: 'center',
                          fontSize: `${dateFontSize}px`,
                          overflow: 'hidden',
                          borderLeft: isMonthBoundary ? '2px solid #1976d2' : '1px solid #ddd'
                        }}
                      >
                        {getShortDayName(dateObj)}
                      </th>
                    );
                  })}
                </tr>
                <tr>
                  {organizedData.dates.map((date, index) => {
                    const dateObj = new Date(date);
                    const isMonthBoundary = monthBoundaries.includes(index);
                    return (
                      <th
                        key={`date-${date}`}
                        style={{
                          border: '1px solid #ddd',
                          padding: '1px',
                          backgroundColor: '#e6f2ff',
                          textAlign: 'center',
                          fontSize: `${dateFontSize}px`,
                          fontWeight: 'bold',
                          overflow: 'hidden',
                          borderLeft: isMonthBoundary ? '2px solid #1976d2' : '1px solid #ddd'
                        }}
                      >
                        {formatShortDate(dateObj)}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {personnelPage.map((row) => (
                  <tr key={row.person.id}>
                    <td style={{ border: '1px solid #ddd', padding: '2px', fontSize: `${bodyFontSize}px`, fontWeight: row.person.isChefService ? 'bold' : 'normal', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <div style={{ color: row.person.isChefService ? '#1976d2' : 'inherit' }}>
                        {row.person.lastName} {row.person.firstName}
                        {row.person.isChefService && (
                          <span style={{
                            fontSize: `${Math.max(5, dateFontSize - 1)}px`,
                            backgroundColor: '#e3f2fd',
                            color: '#1976d2',
                            padding: '1px 2px',
                            borderRadius: '2px',
                            marginLeft: '2px',
                            display: 'inline-block'
                          }}>
                            CS
                          </span>
                        )}
                      </div>
                      {(() => {
                        const personPermanence = permanences.find(p => p.personnel.includes(row.person.id));
                        if (personPermanence?.startTime && personPermanence?.endTime) {
                          return (
                            <div style={{ fontSize: `${Math.max(5, dateFontSize - 1)}px`, color: '#666', marginTop: '1px' }}>
                              {personPermanence.startTime} - {personPermanence.endTime}
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </td>
                    <td style={{ border: '1px solid #ddd', padding: '2px', fontSize: `${Math.max(6, bodyFontSize - 1)}px`, textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.person.grade || '-'}</td>
                    <td style={{ border: '1px solid #ddd', padding: '2px', fontSize: `${Math.max(6, bodyFontSize - 1)}px`, textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <div>{row.person.service || row.person.department || '-'}</div>
                      {row.person.isChefService && (
                        <div style={{ fontSize: `${Math.max(5, dateFontSize - 1)}px`, color: '#1976d2', fontStyle: 'italic' }}>
                          Responsable
                        </div>
                      )}
                    </td>
                    {row.dates.map((isPresent, index) => {
                      const isMonthBoundary = monthBoundaries.includes(index);
                      return (
                        <td
                          key={index}
                          style={{
                            border: '1px solid #ddd',
                            padding: '1px',
                            textAlign: 'center',
                            fontWeight: isPresent ? 'bold' : 'normal',
                            fontSize: `${dateFontSize}px`,
                            borderLeft: isMonthBoundary ? '2px solid #1976d2' : '1px solid #ddd'
                          }}
                        >
                          {isPresent ? '✓' : ''}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Zone d'observations - affichée sur chaque page si des observations existent */}
          {observationsText && (
            <div style={{ 
              marginBottom: '15px',
              width: '100%'
            }}>
              <div style={{ 
                fontWeight: 'bold',
                marginBottom: '3px',
                fontSize: '11px'
              }}>
                Observations :
              </div>
              <div style={{ 
                border: '1px solid #000',
                minHeight: '30px',
                maxHeight: '70px',
                width: '100%',
                padding: '5px',
                boxSizing: 'border-box',
                fontSize: '10px',
                overflowY: 'auto'
              }}>
                {observationsText}
              </div>
            </div>
          )}

          {/* Zone des signatures - affichée sur chaque page */}
          <div style={{ 
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
            marginTop: '8px',
            fontSize: '11px'
          }}>
            <div style={{ flex: 1, marginRight: '10px' }}>
              <div style={{ marginBottom: '4px', fontWeight: 'bold', fontSize: '12px' }}>Le Gestionnaire</div>
              <div style={{ marginBottom: '4px', fontSize: '11px' }}>{gestionnaire || establishmentName || ''}</div>
              <div style={{ border: '2px solid #000', height: '40px', width: '100%' }}></div>
            </div>
            <div style={{ flex: 1, margin: '0 10px' }}>
              <div style={{ marginBottom: '4px', fontWeight: 'bold', fontSize: '12px' }}>Le Proviseur</div>
              <div style={{ marginBottom: '4px', fontSize: '11px' }}>{principal || ''}</div>
              <div style={{ border: '2px solid #000', height: '40px', width: '100%' }}></div>
            </div>
            <div style={{ flex: 1, marginLeft: '10px' }}>
              <div style={{ marginBottom: '4px', fontWeight: 'bold', fontSize: '12px' }}>L'Encadrant</div>
              <div style={{ marginBottom: '4px', fontSize: '11px' }}>{responsableEncadrement || ''}</div>
              <div style={{ border: '2px solid #000', height: '40px', width: '100%' }}></div>
            </div>
          </div>

          {/* Pied de page avec le nom du site */}
          <div style={{ marginTop: '10px', fontSize: '10px', textAlign: 'right', color: '#666', position: 'absolute', bottom: '5px', right: '5px' }}>
            Edugestion.softmarket.io
          </div>
        </div>
      ))}
    </div>
  );
});

const ScheduleManager = () => {
  const { userData, currentUser } = useAuth();
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [selectedPeriodDates, setSelectedPeriodDates] = useState(null);
  const [periodDays, setPeriodDays] = useState([]);
  const [selectedPersonnel, setSelectedPersonnel] = useState([]);
  const [personnelSelections, setPersonnelSelections] = useState(new Map());  // Map<personnelId, Set<dateString>>
  const [personnel, setPersonnel] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');  // État pour stocker le terme de recherche du personnel
  const [observations, setObservations] = useState('');
  const [globalTimeSchedule, setGlobalTimeSchedule] = useState({ startTime: '08:00', endTime: '16:00' });
  const [useGlobalTimeSchedule, setUseGlobalTimeSchedule] = useState(true);
  const [personnelTimeSchedules, setPersonnelTimeSchedules] = useState(new Map());
  const [vacationPeriods, setVacationPeriods] = useState([]);
  const [currentYear, setCurrentYear] = useState('2024-2025');
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [permanences, setPermanences] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [canCreatePermanences, setCanCreatePermanences] = useState(false);
  const [selectedPeriodForPdf, setSelectedPeriodForPdf] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({ open: false, periodId: null, periodName: '' });
  // Ajout des caches pour optimiser les performances
  const [periodsCache, setPeriodsCache] = useState(new Map());
  const [personnelCache, setPersonnelCache] = useState(new Map());
  const pdfComponentRef = useRef(null);
  const [establishmentInfo, setEstablishmentInfo] = useState({
    name: '',
    anneeScolaire: '',
    gestionnaire: '',
    principal: '',
    responsableEncadrement: ''
  });
  const [joursFeries, setJoursFeries] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [selectedPermanenceToEdit, setSelectedPermanenceToEdit] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [isEditingWholePeriod, setIsEditingWholePeriod] = useState(false); // Flag pour distinguer modification générale vs individuelle

  useEffect(() => {
    const fetchUserPermissions = async () => {
      if (currentUser?.uid) {
        try {
          const userDoc = await getDoc(firestoreDoc(db, 'users', currentUser.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setCanCreatePermanences(userData.canCreatePermanences || false);
          }
        } catch (error) {
          console.error("Erreur lors de la récupération des permissions de l'utilisateur:", error);
        }
      }
    };

    fetchUserPermissions();
  }, [currentUser]);

  useEffect(() => {
    const fetchEstablishmentInfo = async () => {
      if (userData?.establishmentId) {
        try {
          const establishmentRef = firestoreDoc(db, 'establishments', userData.establishmentId);
          const establishmentSnap = await getDoc(establishmentRef);
          
          if (establishmentSnap.exists()) {
            const establishmentData = establishmentSnap.data();
            setEstablishmentInfo({
              name: establishmentData.name || '',
              anneeScolaire: establishmentData.anneeScolaire || '',
              gestionnaire: establishmentData.gestionnaire || '',
              principal: establishmentData.principal || '',
              responsableEncadrement: establishmentData.responsableEncadrement || '',
              logo: establishmentData.logo || ''
            });
          }
        } catch (error) {
          console.error('Erreur lors du chargement des informations de l\'établissement:', error);
        }
      }
    };

    fetchEstablishmentInfo();
  }, []);

  useEffect(() => {
    if (userData?.establishmentId) {
      fetchVacationPeriods();
      fetchPersonnel();
      fetchPermanences();
    }
  }, [userData]);

  const fetchVacationPeriods = async () => {
    try {
      const vacationsRef = collection(db, 'establishments', userData.establishmentId, 'vacances');
      const q = query(vacationsRef, orderBy('debut'));
      const querySnapshot = await getDocs(q);
      
      const periods = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        debut: doc.data().debut?.toDate ? doc.data().debut.toDate() : null,
        fin: doc.data().fin?.toDate ? doc.data().fin.toDate() : null,
        nom: doc.data().nom || 'Période sans nom'
      }));
      
      setVacationPeriods(periods);
    } catch (error) {
      setError("Erreur lors du chargement des périodes de vacances");
    }
  };

  const fetchPersonnel = async () => {
    try {
      if (!userData?.establishmentId) {
        setError("Erreur: establishmentId manquant");
        return;
      }

      const usersRef = collection(db, 'users');
      
      // Récupérer le personnel régulier
      const personnelQuery = query(
        usersRef, 
        where('establishmentId', '==', userData.establishmentId),
        where('role', '==', 'personnel'),
        where('status', '==', 'actif')
      );
      
      const personnelSnapshot = await getDocs(personnelQuery);
      
      // Récupérer les chefs de service
      const chefsQuery = query(
        usersRef, 
        where('establishmentId', '==', userData.establishmentId),
        where('role', '==', 'chefService'),
        where('status', '==', 'actif')
      );
      
      const chefsSnapshot = await getDocs(chefsQuery);
      
      if (personnelSnapshot.empty && chefsSnapshot.empty) {
        setError("Aucun personnel ou chef de service trouvé pour cet établissement");
      }

      const personnelList = [];
      
      // Ajouter le personnel régulier
      personnelSnapshot.forEach(doc => {
        const data = doc.data();
        personnelList.push({
          id: doc.id,
          ...data,
          isChefService: false
        });
      });
      
      // Ajouter les chefs de service
      chefsSnapshot.forEach(doc => {
        const data = doc.data();
        personnelList.push({
          id: doc.id,
          ...data,
          isChefService: true
        });
      });
      
      // Trier le personnel par ordre alphabétique (nom puis prénom)
      personnelList.sort((a, b) => {
        const lastNameA = (a.lastName || '').toLowerCase();
        const lastNameB = (b.lastName || '').toLowerCase();
        
        if (lastNameA < lastNameB) return -1;
        if (lastNameA > lastNameB) return 1;
        
        const firstNameA = (a.firstName || '').toLowerCase();
        const firstNameB = (b.firstName || '').toLowerCase();
        
        if (firstNameA < firstNameB) return -1;
        if (firstNameA > firstNameB) return 1;
        
        return 0;
      });
      
      
      setPersonnel(personnelList);
      
    } catch (error) {
      console.error('Erreur dans fetchPersonnel:', error);
      setError("Erreur lors du chargement du personnel");
    }
  };

  const fetchPermanences = async () => {
    try {
      setLoading(true);
      
      // 1. Requête pour récupérer toutes les permanences de l'établissement
      const permanencesRef = collection(db, 'permanences');
      const q = query(
        permanencesRef,
        where('establishmentId', '==', userData.establishmentId)
      );
      
      const querySnapshot = await getDocs(q);
      
      // 3. Les chefs de service voient toutes les permanences de l'établissement (comme les admins)
      let filteredDocs = querySnapshot.docs;
      
      // 4. Tri des résultats par date de création décroissante
      filteredDocs.sort((a, b) => {
        const dateA = a.data().createdAt;
        const dateB = b.data().createdAt;
        
        // Gérer les cas où createdAt pourrait être null ou undefined
        if (!dateA) return 1;
        if (!dateB) return -1;
        
        // Convertir les timestamps Firestore en millisecondes
        const timeA = dateA.toMillis ? dateA.toMillis() : dateA;
        const timeB = dateB.toMillis ? dateB.toMillis() : dateB;
        
        return timeB - timeA; // Ordre décroissant
      });
      
      // Collecter tous les IDs de périodes et de personnel nécessaires
      const periodIds = new Set();
      const personnelIds = new Set();
      
      filteredDocs.forEach(doc => {
        const permanence = doc.data();
        periodIds.add(permanence.period);
        permanence.personnel.forEach(personId => personnelIds.add(personId));
      });
      
      // Récupérer toutes les périodes manquantes en une seule fois
      const newPeriodsCache = new Map(periodsCache);
      const periodPromises = [];
      
      for (const periodId of periodIds) {
        if (!newPeriodsCache.has(periodId)) {
          periodPromises.push(
            getDoc(firestoreDoc(db, 'establishments', userData.establishmentId, 'vacances', periodId))
              .then(doc => {
                if (doc.exists()) {
                  newPeriodsCache.set(periodId, doc.data());
                } else {
                  newPeriodsCache.set(periodId, { nom: 'Période inconnue' });
                }
              })
          );
        }
      }
      
      // Récupérer tout le personnel manquant en une seule fois
      const newPersonnelCache = new Map(personnelCache);
      const personnelPromises = [];
      
      for (const personId of personnelIds) {
        if (!newPersonnelCache.has(personId)) {
          personnelPromises.push(
            getDoc(firestoreDoc(db, 'users', personId))
              .then(doc => {
                if (doc.exists()) {
                  newPersonnelCache.set(personId, { id: doc.id, ...doc.data() });
                } else {
                  newPersonnelCache.set(personId, null);
                }
              })
          );
        }
      }
      
      // Attendre que toutes les requêtes soient terminées
      await Promise.all([...periodPromises, ...personnelPromises]);
      
      // Mettre à jour les caches
      setPeriodsCache(newPeriodsCache);
      setPersonnelCache(newPersonnelCache);
      
      // Construire la liste des permanences avec les données en cache
      const permanencesList = filteredDocs
        .map(docSnapshot => {
          const permanence = docSnapshot.data();
          
          // S'assurer que les jours sont correctement formatés comme un tableau
          let days = [];
          if (permanence.days) {
            if (Array.isArray(permanence.days)) {
              days = [...permanence.days];
            } else if (typeof permanence.days === 'object' && permanence.days !== null) {
              try {
                days = Array.from(permanence.days);
              } catch (err) {
                console.error('Erreur lors de la conversion des jours en tableau:', err);
                days = [];
              }
            }
          }
          
          const periodData = newPeriodsCache.get(permanence.period);
          const personnelInfo = permanence.personnel
            .map(personId => newPersonnelCache.get(personId))
            .filter(p => p !== null);
          
          return {
            id: docSnapshot.id,
            ...permanence,
            days: days,
            periodName: periodData?.nom || 'Période inconnue',
            personnelInfo: personnelInfo
          };
        })
        .filter(permanence => {
          // Filtrer les permanences invalides (même logique que dans le compte Admin)
          return permanence.days && 
                 permanence.days.length > 0 && 
                 permanence.personnel && 
                 permanence.personnel.length > 0 &&
                 permanence.period; // S'assurer qu'il y a une période valide
        });
      
      setPermanences(permanencesList);
      setLoading(false);
    } catch (error) {
      console.error("Erreur lors du chargement des permanences:", error);
      setError("Erreur lors du chargement des permanences");
      setLoading(false);
    }
  };

  // Fonction pour grouper les permanences par période
  const groupPermanencesByPeriod = (permanences) => {
    const groups = new Map();
    
    permanences.forEach(permanence => {
      if (!groups.has(permanence.period)) {
        groups.set(permanence.period, []);
      }
      groups.get(permanence.period).push(permanence);
    });
    
    return Array.from(groups.entries()).map(([periodId, perms]) => ({
      periodId,
      periodName: perms[0].periodName,
      permanences: perms
    }));
  };

  // Fonction pour vérifier si une date est un jour férié
  const isJourFerie = (date) => {
    if (!date) return false;
    
    const dateStr = date instanceof Date ? 
      date.toISOString().split('T')[0] : 
      new Date(date).toISOString().split('T')[0];
    
    return joursFeries.find(j => {
      const jourStr = j.date instanceof Date ? 
        j.date.toISOString().split('T')[0] : 
        new Date(j.date).toISOString().split('T')[0];
      return jourStr === dateStr;
    });
  };

  const handlePeriodChange = (event) => {
    const periodId = event.target.value;
    setSelectedPeriod(periodId);
    
    // Réinitialiser les sélections
    setPersonnelSelections(new Map());
    setPeriodDays([]);
    
    // Trouver la période sélectionnée
    const period = vacationPeriods.find(p => p.id === periodId);
    
    if (period && period.debut && period.fin) {
      // Convertir les timestamps en objets Date si nécessaire
      const debutDate = typeof period.debut.toDate === 'function' ? period.debut.toDate() : new Date(period.debut);
      const finDate = typeof period.fin.toDate === 'function' ? period.fin.toDate() : new Date(period.fin);
      
      // Stocker les dates de début et de fin
      setSelectedPeriodDates({ debut: debutDate, fin: finDate });
      
      // Générer toutes les dates dans la plage
      const dates = [];
      const currentDate = new Date(debutDate);
      
      while (currentDate <= finDate) {
        dates.push(new Date(currentDate));
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      // Mettre à jour les jours disponibles
      setPeriodDays(dates);
    }
  };

  const handlePersonnelChange = (event) => {
    const selectedIds = event.target.value;
    setSelectedPersonnel(selectedIds);
    
    // Mettre à jour les sélections de personnel
    const newSelections = new Map(personnelSelections);
    
    // Ajouter les nouveaux personnels sélectionnés
    selectedIds.forEach(id => {
      if (!newSelections.has(id)) {
        newSelections.set(id, []);
      }
    });
    
    // Supprimer les personnels désélectionnés
    Array.from(newSelections.keys()).forEach(id => {
      if (!selectedIds.includes(id)) {
        newSelections.delete(id);
      }
    });
    
    setPersonnelSelections(newSelections);
  };
  
  // Fonction pour gérer la sélection d'une cellule dans le tableau
  const handleCellSelection = (personnelId, dateStr) => {
    const newSelections = new Map(personnelSelections);
    const currentSelections = newSelections.get(personnelId) || [];
    
    // Vérifier si currentSelections est un Set
    if (currentSelections instanceof Set) {
      // Créer une nouvelle copie du Set
      const newSet = new Set(currentSelections);
      
      // Basculer la sélection avec les méthodes de Set
      if (newSet.has(dateStr)) {
        newSet.delete(dateStr);
      } else {
        newSet.add(dateStr);
      }
      
      newSelections.set(personnelId, newSet);
    } else {
      // Traiter comme un tableau
      if (Array.isArray(currentSelections) && currentSelections.includes(dateStr)) {
        newSelections.set(
          personnelId, 
          currentSelections.filter(d => d !== dateStr)
        );
      } else {
        newSelections.set(
          personnelId, 
          [...(Array.isArray(currentSelections) ? currentSelections : []), dateStr]
        );
      }
    }
    
    setPersonnelSelections(newSelections);
  };
  
  // Fonction pour gérer la sélection de personnel dans la nouvelle interface
  const handlePersonnelSelection = (personId) => {
    setSelectedPersonnel(prev => {
      if (prev.includes(personId)) {
        // Retirer le personnel de la sélection
        const newSelected = prev.filter(id => id !== personId);
        // Supprimer les sélections de dates pour ce personnel
        setPersonnelSelections(prevSelections => {
          const newSelections = new Map(prevSelections);
          newSelections.delete(personId);
          return newSelections;
        });
        return newSelected;
      } else {
        // Ajouter le personnel à la sélection
        const newSelected = [...prev, personId];
        // Initialiser les sélections de dates pour ce personnel
        setPersonnelSelections(prevSelections => {
          const newSelections = new Map(prevSelections);
          newSelections.set(personId, new Set());
          return newSelections;
        });
        // Initialiser les horaires pour ce personnel
        setPersonnelTimeSchedules(prevSchedules => {
          const newSchedules = new Map(prevSchedules);
          if (!newSchedules.has(personId)) {
            newSchedules.set(personId, { ...globalTimeSchedule });
          }
          return newSchedules;
        });
        return newSelected;
      }
    });
  };
  
  // Fonction pour gérer la sélection de dates dans la nouvelle interface
  const handleDateSelection = (personId, dateStr) => {
    if (!selectedPersonnel.includes(personId)) return;
    
    // La date est déjà normalisée quand elle est passée à cette fonction
    const normalizedDateStr = dateStr;
    
    setPersonnelSelections(prev => {
      const newSelections = new Map(prev);
      const personDates = newSelections.get(personId) || new Set();
      
      // Vérifier si la date existe déjà (en tenant compte des deux formats possibles)
      const hasDate = Array.from(personDates).some(d => {
        const normalizedDate = normalizeDateFormat(d);
        return normalizedDate === normalizedDateStr;
      });
      
      if (hasDate) {
        // Supprimer toutes les versions de cette date
        Array.from(personDates).forEach(d => {
          const normalizedD = normalizeDateFormat(d);
          if (normalizedD === normalizedDateStr) {
            personDates.delete(d);
          }
        });
      } else {
        // Ajouter la date normalisée
        personDates.add(normalizedDateStr);
      }
      
      newSelections.set(personId, personDates);
      return newSelections;
    });
  };
  
  // Fonction pour gérer la sélection d'une ligne entière (tous les jours pour un personnel)
  const handleRowSelection = (personId) => {
    setPersonnelSelections(prev => {
      const newSelections = new Map(prev);
      const personDates = newSelections.get(personId) || new Set();
      
      // Normaliser les dates de la période avec notre fonction utilitaire
      const normalizedPeriodDays = periodDays.map(date => normalizeDateFormat(date));
      
      // Vérifier si tous les jours sont déjà sélectionnés
      const allDatesSelected = normalizedPeriodDays.every(normalizedDateStr => {
        return Array.from(personDates).some(d => {
          const normalizedDate = normalizeDateFormat(d);
          return normalizedDate === normalizedDateStr;
        });
      });
      
      if (allDatesSelected) {
        // Si tous les jours sont déjà sélectionnés, désélectionner tous
        newSelections.set(personId, new Set());
      } else {
        // Sinon, sélectionner tous les jours
        newSelections.set(personId, new Set(normalizedPeriodDays));
      }
      
      return newSelections;
    });
  };
  
  // Fonction pour gérer la sélection d'une colonne entière (un jour pour tous les personnels)
  const handleColumnSelection = (date) => {
    // Normaliser le format de la date (YYYY-MM-DD) avec notre fonction utilitaire
    const normalizedDateStr = normalizeDateFormat(date);
    
    // Si aucun personnel n'est sélectionné, sélectionner automatiquement tout le personnel
    if (selectedPersonnel.length === 0) {
      setSelectedPersonnel(personnel.map(p => p.id));
    }
    
    // Créer un ensemble de dates globales sélectionnées pour l'étape 2
    const globalSelectedDates = new Set();
    periodDays.forEach(day => {
      const dayStr = normalizeDateFormat(day);
      // Vérifier si cette date est déjà sélectionnée pour au moins un membre du personnel
      const isSelected = Array.from(personnelSelections.values()).some(personDates => {
        if (personDates instanceof Set) {
          return Array.from(personDates).some(d => {
            const normalizedDate = normalizeDateFormat(d);
            return normalizedDate === dayStr;
          });
        }
        return false;
      });
      if (isSelected) {
        globalSelectedDates.add(dayStr);
      }
    });
    
    // Vérifier si la date cliquée est déjà sélectionnée globalement
    const isDateSelected = globalSelectedDates.has(normalizedDateStr);
    
    setPersonnelSelections(prev => {
      const newSelections = new Map(prev);
      
      // Sélectionner tous les membres du personnel s'ils ne sont pas déjà sélectionnés
      personnel.forEach(person => {
        if (!newSelections.has(person.id)) {
          newSelections.set(person.id, new Set());
        }
      });
      
      // Basculer la sélection pour tous les personnels sélectionnés
      selectedPersonnel.forEach(personId => {
        const personDates = newSelections.get(personId) || new Set();
        
        if (isDateSelected) {
          // Désélectionner la date pour tous
          Array.from(personDates).forEach(d => {
            const normalizedD = normalizeDateFormat(d);
            if (normalizedD === normalizedDateStr) {
              personDates.delete(d);
            }
          });
        } else {
          // Sélectionner la date pour tous
          personDates.add(normalizedDateStr);
        }
        
        newSelections.set(personId, personDates);
      });
      
      return newSelections;
    });
  };

  const handlePrint = async (periodGroup) => {
    try {
      // Vérifier que les données nécessaires sont disponibles
      if (!pdfComponentRef.current) {
        showSnackbar('Erreur: Impossible de générer le PDF', 'error');
        return;
      }
      
      setLoading(true);
      showSnackbar('Génération du PDF en cours...', 'info');
      
      // Si un groupe de période est passé, le définir comme sélectionné pour le PDF
      if (periodGroup) {
        const pdfData = {
          periodName: periodGroup.periodName,
          permanences: periodGroup.permanences,
          personnel: personnel,
          establishmentName: establishmentInfo?.name || userData?.establishmentName,
          anneeScolaire: establishmentInfo?.anneeScolaire || userData?.anneeScolaire,
          responsableEncadrement: establishmentInfo?.responsableEncadrement || userData?.responsableEncadrement,
          observations: periodGroup.permanences[0]?.observations || '',
          gestionnaire: establishmentInfo?.gestionnaire || '',
          principal: establishmentInfo?.principal || '',
          logo: establishmentInfo?.logo || ''
        };
        
        setSelectedPeriodForPdf(pdfData);
      }

      // Attendre plus longtemps que le composant PDF soit complètement rendu
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Créer un nouveau document PDF
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true, // Compression pour réduire la taille du PDF
        hotfixes: ['px_scaling'] // Correction pour le scaling des pixels
      });
      
      // Obtenir toutes les pages directement à partir du DOM
      const pdfContainer = pdfComponentRef.current;
      const pageElements = Array.from(pdfContainer.children).filter(el => 
        el.className && el.className.includes('pdf-page')
      );
      
      // Vérifier si des pages ont été trouvées
      if (!pageElements || pageElements.length === 0) {
        console.error('Aucune page trouvée dans le composant PDF');
        
        // Si nous ne trouvons pas de pages avec la classe, essayons de capturer tout le conteneur
        try {
          const canvas = await html2canvas(pdfContainer, {
            scale: 2,
            backgroundColor: '#ffffff',
            logging: true, // Activer les logs pour déboguer
            useCORS: true,
            allowTaint: true
          });
          
          const imgData = canvas.toDataURL('image/png');
          const pageWidth = pdf.internal.pageSize.getWidth();
          const pageHeight = pdf.internal.pageSize.getHeight();
          
          // Ajuster pour tenir sur une page A4
          const margin = 2;
          pdf.addImage(imgData, 'PNG', margin, margin, pageWidth - (margin * 2), pageHeight - (margin * 2));
          
          // Enregistrer le PDF même si nous avons dû utiliser la méthode de secours
          const fileName = selectedPeriodForPdf && selectedPeriodForPdf.periodName 
            ? `permanences_${selectedPeriodForPdf.periodName.replace(/\s+/g, '_')}.pdf`
            : 'permanences.pdf';
          
          pdf.save(fileName);
          showSnackbar('PDF généré avec succès (méthode alternative)', 'success');
          setLoading(false);
          return;
        } catch (fallbackError) {
          console.error('Erreur lors de la tentative de secours:', fallbackError);
          showSnackbar('Erreur lors de la génération du PDF', 'error');
          setLoading(false);
          return;
        }
      }
      
      // Pour chaque page, créer une image et l'ajouter au PDF
      const pageCount = pageElements.length;
      
      for (let i = 0; i < pageCount; i++) {
        const pageElement = pageElements[i];
        
        // Ajouter une nouvelle page pour chaque page après la première
        if (i > 0) {
          pdf.addPage();
        }
        
        try {
          // Capturer la page actuelle
          const canvas = await html2canvas(pageElement, {
            scale: 2,
            backgroundColor: '#ffffff',
            logging: false,
            useCORS: true,
            allowTaint: true,
            width: 210 * 3.78, // 210mm en pixels (3.78 pixels/mm)
            height: 297 * 3.78 // 297mm en pixels
          });
          
          const imgData = canvas.toDataURL('image/png');
          const pageWidth = pdf.internal.pageSize.getWidth();
          const pageHeight = pdf.internal.pageSize.getHeight();
          
          // Ajuster les dimensions pour que l'image tienne parfaitement sur la page A4
          const margin = 2;
          const imgWidth = pageWidth - (margin * 2);
          const imgHeight = (canvas.height * imgWidth) / canvas.width;
          
          // Vérifier si la hauteur calculée dépasse la hauteur de la page
          if (imgHeight <= pageHeight - (margin * 2)) {
            // L'image tient en hauteur, on la centre verticalement
            const yPosition = (pageHeight - imgHeight) / 2;
            pdf.addImage(imgData, 'PNG', margin, yPosition, imgWidth, imgHeight);
          } else {
            // L'image est trop haute, on la redimensionne pour qu'elle tienne en hauteur
            const adjustedHeight = pageHeight - (margin * 2);
            const adjustedWidth = (canvas.width * adjustedHeight) / canvas.height;
            const xPosition = (pageWidth - adjustedWidth) / 2;
            pdf.addImage(imgData, 'PNG', xPosition, margin, adjustedWidth, adjustedHeight);
          }
        } catch (pageError) {
          console.error(`Erreur lors de la capture de la page ${i+1}:`, pageError);
          // Continuer avec les autres pages même si une page échoue
        }
      }
      
      // S'assurer que selectedPeriodForPdf existe et a une propriété periodName
      const fileName = selectedPeriodForPdf && selectedPeriodForPdf.periodName 
        ? `permanences_${selectedPeriodForPdf.periodName.replace(/\s+/g, '_')}.pdf`
        : 'permanences.pdf';
      
      // Enregistrer le PDF
      pdf.save(fileName);
      showSnackbar('PDF généré avec succès', 'success');
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
      showSnackbar('Erreur lors de la génération du PDF', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour rendre une cellule de date dans le tableau
  const renderDateCell = (date) => {
    const formattedDate = date.toLocaleDateString('fr-FR', { 
      weekday: 'short', 
      day: 'numeric'
    });
    
    const jourFerie = isJourFerie(date);
    const isWeekend = date.getDay() === 0 || date.getDay() === 6;
    
    return (
      <TableCell 
        key={date.toISOString()} 
        align="center"
        onClick={() => handleColumnSelection(date)}
        title={jourFerie ? `${jourFerie.nom}` : 'Cliquer pour sélectionner/désélectionner toute la colonne'}
        sx={{
          backgroundColor: isWeekend ? '#f5f5f5' : (jourFerie ? jourFerie.couleur : 'inherit'),
          position: 'relative',
          minWidth: '80px',
          padding: '8px 4px',
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: isWeekend ? '#eeeeee' : (jourFerie ? jourFerie.couleur : '#f5f5f5'),
            opacity: 0.8
          },
          borderBottom: '1px solid #e0e0e0',
          borderRight: '1px solid #e0e0e0',
          fontWeight: 'bold'
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
            {formattedDate}
          </Typography>
          {jourFerie && (
            <Chip 
              label={jourFerie.nom} 
              size="small" 
              sx={{ 
                fontSize: '0.6rem', 
                height: '16px',
                maxWidth: '70px',
                mt: 0.5,
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                overflow: 'hidden'
              }} 
            />
          )}
        </Box>
      </TableCell>
    );
  };
  
  // Fonction pour normaliser le format de date au format YYYY-MM-DD
  const normalizeDateFormat = (date) => {
    if (!date) return '';
    
    try {
      // Si c'est déjà une chaîne au format YYYY-MM-DD
      if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
        return date;
      }
      
      // Si c'est une chaîne ISO avec heure, extraire juste la date
      if (typeof date === 'string' && date.includes('T')) {
        return date.split('T')[0];
      }
      
      // Si c'est un objet Date
      if (date instanceof Date) {
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      }
      
      // Si c'est un timestamp ou une chaîne non ISO
      const d = new Date(date);
      if (!isNaN(d.getTime())) {
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      }
      
      return '';
    } catch (error) {
      console.error('Erreur lors de la normalisation de la date:', error);
      return '';
    }
  };

  // Fonction pour vérifier si une date est dans un ensemble de dates normalisées
  const isDateInSet = (dateStr, dateSet) => {
    if (!dateSet || !(dateSet instanceof Set) || dateSet.size === 0) return false;
    
    // Normaliser la date à vérifier
    const normalizedDateStr = normalizeDateFormat(dateStr);
    if (!normalizedDateStr) return false;
    
    // Vérifier directement si la date normalisée est dans l'ensemble
    if (dateSet.has(normalizedDateStr)) return true;
    
    // Si la date n'est pas trouvée directement, vérifier avec normalisation
    return Array.from(dateSet).some(d => {
      const normalizedD = normalizeDateFormat(d);
      return normalizedD === normalizedDateStr;
    });
  };

  const handleCreatePermanence = async () => {
    try {
      setLoading(true);
      
      if (!selectedPeriod) {
        throw new Error("Veuillez sélectionner une période de vacances");
      }

      if (selectedPersonnel.length === 0) {
        throw new Error("Veuillez sélectionner au moins un membre du personnel");
      }


      // Créer une permanence pour chaque personnel sélectionné
      const permanencesToCreate = selectedPersonnel.map(personId => {
        // Récupérer les dates sélectionnées pour ce personnel
        const selectedDates = personnelSelections.get(personId);
        
        // Normaliser le format des dates (YYYY-MM-DD)
        const personDates = selectedDates ? Array.from(selectedDates).map(date => {
          return normalizeDateFormat(date);
        }) : [];
        
        // Récupérer les horaires pour ce personnel ou utiliser les horaires globaux
        const timeSchedule = useGlobalTimeSchedule 
          ? globalTimeSchedule 
          : personnelTimeSchedules.get(personId) || globalTimeSchedule;
        
        return {
          period: selectedPeriod,
          personnel: [personId],
          days: personDates,
          startTime: timeSchedule.startTime,
          endTime: timeSchedule.endTime,
          observations,
          establishmentId: userData.establishmentId,
          schoolYear: currentYear,
          createdAt: serverTimestamp()
        };
      }).filter(p => p.days.length > 0);


      if (editMode && selectedPermanenceToEdit) {
        if (isEditingWholePeriod) {
          // Mode modification GÉNÉRALE : supprimer toutes les anciennes permanences de la période et créer les nouvelles
          
          // 1. Récupérer toutes les permanences existantes pour cette période
          const permanencesRef = collection(db, 'permanences');
          const q = query(
            permanencesRef,
            where('establishmentId', '==', userData.establishmentId),
            where('period', '==', selectedPeriod)
          );
          const existingPermanencesSnapshot = await getDocs(q);
          
          
          // 2. Supprimer toutes les anciennes permanences de cette période
          const deletePromises = existingPermanencesSnapshot.docs.map(doc => 
            deleteDoc(firestoreDoc(db, 'permanences', doc.id))
          );
          await Promise.all(deletePromises);
          
          // 3. Créer les nouvelles permanences avec les données mises à jour
          
          // Ajouter le champ visibleToPersonnel à toutes les permanences
          const permanencesWithVisibility = permanencesToCreate.map(permanence => ({
            ...permanence,
            visibleToPersonnel: true
          }));
          
          // Créer toutes les nouvelles permanences
          await Promise.all(
            permanencesWithVisibility.map(permanence => 
              addDoc(collection(db, 'permanences'), permanence)
            )
          );
          
          
          showSnackbar('Les permanences de la période ont été mises à jour avec succès', 'success');
        } else {
          // Mode modification INDIVIDUELLE : mettre à jour uniquement la permanence sélectionnée
          
          // Récupérer les horaires (pour le premier personnel ou global)
          const personId = selectedPersonnel[0] || selectedPermanenceToEdit.personnel[0];
          const timeSchedule = useGlobalTimeSchedule 
            ? globalTimeSchedule 
            : personnelTimeSchedules.get(personId) || globalTimeSchedule;
          
          // Récupérer les dates pour le premier personnel
          const selectedDates = personnelSelections.get(personId);
          
          // Normaliser le format des dates (YYYY-MM-DD)
          const personDates = selectedDates ? Array.from(selectedDates).map(date => {
            return normalizeDateFormat(date);
          }) : [];
          
          
          // Mettre à jour uniquement cette permanence
          await updateDoc(firestoreDoc(db, 'permanences', selectedPermanenceToEdit.id), {
            personnel: selectedPersonnel,
            days: personDates,
            startTime: timeSchedule.startTime,
            endTime: timeSchedule.endTime,
            observations: observations,
            updatedAt: serverTimestamp(),
            visibleToPersonnel: true
          });
          
          showSnackbar('La permanence a été mise à jour avec succès', 'success');
        }
        
        // Vider les caches pour forcer le rechargement des données
        setPeriodsCache(new Map());
        setPersonnelCache(new Map());
        
        // Recharger les permanences pour voir les modifications
        await fetchPermanences();
        
        // Réinitialiser le formulaire et fermer la boîte de dialogue
        setSelectedPeriod('');
        setSelectedPeriodDates(null);
        setPeriodDays([]);
        setSelectedPersonnel([]);
        setPersonnelSelections(new Map());
        setObservations('');
        setGlobalTimeSchedule({ startTime: '08:00', endTime: '16:00' });
        setUseGlobalTimeSchedule(true);
        setPersonnelTimeSchedules(new Map());
        setEditMode(false);
        setSelectedPermanenceToEdit(null);
        setIsEditingWholePeriod(false);
        setOpenViewDialog(false);
      } else {
        // Mode création : ajouter de nouvelles permanences
        
        // En mode création, vérifier qu'au moins une date est sélectionnée
        if (permanencesToCreate.length === 0) {
          throw new Error("Veuillez sélectionner au moins une date pour un personnel");
        }
        
        // Ajouter le champ visibleToPersonnel à toutes les permanences
        const permanencesWithVisibility = permanencesToCreate.map(permanence => ({
          ...permanence,
          visibleToPersonnel: true
        }));
        
        await Promise.all(
          permanencesWithVisibility.map(permanence => 
            addDoc(collection(db, 'permanences'), permanence)
          )
        );
        
        // Vider les caches pour forcer le rechargement des données
        setPeriodsCache(new Map());
        setPersonnelCache(new Map());
        
        // Recharger les permanences pour voir les nouvelles entrées
        await fetchPermanences();
        
        // Réinitialiser le formulaire et fermer la boîte de dialogue
        setSelectedPeriod('');
        setSelectedPeriodDates(null);
        setPeriodDays([]);
        setSelectedPersonnel([]);
        setPersonnelSelections(new Map());
        setObservations('');
        setGlobalTimeSchedule({ startTime: '08:00', endTime: '16:00' });
        setUseGlobalTimeSchedule(true);
        setPersonnelTimeSchedules(new Map());
        setOpenViewDialog(false); // Fermer la boîte de dialogue
        
        showSnackbar('Les permanences ont été créées avec succès', 'success');
      }
      
    } catch (error) {
      console.error('Erreur lors de la création/modification des permanences:', error);
      setError(error.message || 'Erreur lors de la création/modification des permanences');
      showSnackbar(error.message || 'Erreur lors de la création/modification des permanences', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleOpenCreateDialog = () => {
    setEditMode(false);
    setSelectedPermanenceToEdit(null);
    setIsEditingWholePeriod(false);
    setOpenViewDialog(true);
  };
  
  // Fonction pour générer les jours entre deux dates
  const generateDaysBetweenDates = (startDate, endDate) => {
    if (!startDate || !endDate) {
      console.error('Dates invalides dans generateDaysBetweenDates:', { startDate, endDate });
      return [];
    }
    
    try {
      const dates = [];
      
      // S'assurer que nous travaillons avec des objets Date
      let currentDate, lastDate;
      
      // Traitement sécurisé de la date de début
      if (startDate instanceof Date) {
        currentDate = new Date(startDate.getTime()); // Cloner la date
      } else if (typeof startDate === 'string' || typeof startDate === 'number') {
        currentDate = new Date(startDate);
      } else if (startDate && typeof startDate.toDate === 'function') {
        currentDate = startDate.toDate();
      } else {
        console.error('Format de date de début non reconnu:', startDate);
        return [];
      }
      
      // Traitement sécurisé de la date de fin
      if (endDate instanceof Date) {
        lastDate = new Date(endDate.getTime()); // Cloner la date
      } else if (typeof endDate === 'string' || typeof endDate === 'number') {
        lastDate = new Date(endDate);
      } else if (endDate && typeof endDate.toDate === 'function') {
        lastDate = endDate.toDate();
      } else {
        console.error('Format de date de fin non reconnu:', endDate);
        return [];
      }
      
      // Vérifier que les dates sont valides
      if (isNaN(currentDate.getTime()) || isNaN(lastDate.getTime())) {
        console.error('Dates invalides après conversion dans generateDaysBetweenDates');
        return [];
      }
      
      // Réinitialiser l'heure pour éviter les problèmes de comparaison
      currentDate.setHours(0, 0, 0, 0);
      lastDate.setHours(0, 0, 0, 0);
      
      // Ajouter un jour à la date de fin pour inclure cette date dans la plage
      lastDate.setDate(lastDate.getDate() + 1);
      
      // Limiter le nombre de jours à 60 pour éviter les boucles infinies
      let safetyCounter = 0;
      const MAX_DAYS = 60;
      
      while (currentDate < lastDate && safetyCounter < MAX_DAYS) {
        dates.push(new Date(currentDate));
        currentDate.setDate(currentDate.getDate() + 1);
        safetyCounter++;
      }
      
      return dates;
    } catch (error) {
      console.error('Erreur dans generateDaysBetweenDates:', error);
      return [];
    }
  };

  // Fonction pour ouvrir le dialogue de modification générale (toute la période)
  const handleOpenEditDialogGeneral = (periodPermanences, periodId) => {
    setEditMode(true);
    setIsEditingWholePeriod(true); // Activer le flag de modification générale
    // Pour la modification générale, on utilise la première permanence comme référence
    setSelectedPermanenceToEdit(periodPermanences[0]);
    
    // Récupérer la période
    setSelectedPeriod(periodId);
    
    
    // Réinitialiser les sélections avant de les configurer
    setPersonnelSelections(new Map());
    
    // Récupérer les horaires de la première permanence (généralement identiques)
    if (periodPermanences[0].startTime && periodPermanences[0].endTime) {
      setGlobalTimeSchedule({
        startTime: periodPermanences[0].startTime,
        endTime: periodPermanences[0].endTime
      });
    }
    
    // Trouver la période dans les données
    const selectedVacation = vacationPeriods.find(period => period.id === periodId);
    
    if (selectedVacation) {
      // Configurer les dates de la période
      setSelectedPeriodDates({
        debut: selectedVacation.debut,
        fin: selectedVacation.fin
      });
      
      // Générer les jours de la période
      let debutDate, finDate;
      
      try {
        if (selectedVacation.debut && typeof selectedVacation.debut.toDate === 'function') {
          debutDate = selectedVacation.debut.toDate();
        } else if (selectedVacation.debutDate) {
          debutDate = selectedVacation.debutDate;
        } else {
          debutDate = new Date(selectedVacation.debut);
        }
      } catch (error) {
        console.error('Erreur lors de la conversion de la date de début:', error);
        debutDate = new Date();
      }
      
      try {
        if (selectedVacation.fin && typeof selectedVacation.fin.toDate === 'function') {
          finDate = selectedVacation.fin.toDate();
        } else if (selectedVacation.finDate) {
          finDate = selectedVacation.finDate;
        } else {
          finDate = new Date(selectedVacation.fin);
        }
      } catch (error) {
        console.error('Erreur lors de la conversion de la date de fin:', error);
        finDate = new Date();
      }
      
      if (!isNaN(debutDate.getTime()) && !isNaN(finDate.getTime())) {
        const days = generateDaysBetweenDates(debutDate, finDate);
        setPeriodDays(days);
        
        // Collecter tous les personnels de toutes les permanences avec leurs informations
        const personnelMap = new Map();
        periodPermanences.forEach(perm => {
          perm.personnel.forEach(personId => {
            if (!personnelMap.has(personId) && perm.personnelInfo) {
              // Trouver les détails du personnel dans la permanence
              const personDetails = perm.personnelInfo.find(p => p.id === personId);
              if (personDetails) {
                personnelMap.set(personId, personDetails);
              }
            }
          });
        });
        
        
        // Trier les personnels par ordre alphabétique
        const sortedPersonnelIds = Array.from(personnelMap.keys()).sort((a, b) => {
          const personA = personnelMap.get(a);
          const personB = personnelMap.get(b);
          
          if (!personA || !personB) return 0;
          
          const lastNameA = (personA.lastName || '').toLowerCase();
          const lastNameB = (personB.lastName || '').toLowerCase();
          
          if (lastNameA < lastNameB) return -1;
          if (lastNameA > lastNameB) return 1;
          
          const firstNameA = (personA.firstName || '').toLowerCase();
          const firstNameB = (personB.firstName || '').toLowerCase();
          
          if (firstNameA < firstNameB) return -1;
          if (firstNameB > firstNameA) return 1;
          
          return 0;
        });
        
        
        setSelectedPersonnel(sortedPersonnelIds);
        
        // Configurer les sélections de dates pour chaque personnel
        
        // Créer une nouvelle Map pour forcer la mise à jour du state
        const newSelections = new Map();
        const newPersonnelTimeSchedules = new Map();
        
        // Pour chaque permanence, récupérer les dates de chaque personnel
        periodPermanences.forEach(permanence => {
          permanence.personnel.forEach(personId => {
            // Initialiser le Set de dates pour ce personnel s'il n'existe pas
            if (!newSelections.has(personId)) {
              newSelections.set(personId, new Set());
            }
            
            // Initialiser les horaires pour ce personnel
            if (permanence.startTime && permanence.endTime) {
              newPersonnelTimeSchedules.set(personId, {
                startTime: permanence.startTime,
                endTime: permanence.endTime
              });
            }
            
            const dateSet = newSelections.get(personId);
            
            // Ajouter les dates de cette permanence au Set du personnel
            if (Array.isArray(permanence.days)) {
              permanence.days.forEach(day => {
                const normalizedDay = normalizeDateFormat(day);
                if (normalizedDay) {
                  dateSet.add(normalizedDay);
                }
              });
            } else if (permanence.days && typeof permanence.days === 'object') {
              try {
                Object.values(permanence.days).forEach(day => {
                  const normalizedDay = normalizeDateFormat(day);
                  if (normalizedDay) {
                    dateSet.add(normalizedDay);
                  }
                });
              } catch (err) {
                console.error('Erreur lors de la conversion des jours:', err);
              }
            }
            
          });
        });
        
        // Mettre à jour l'état avec la nouvelle Map
        setPersonnelSelections(newSelections);
        setPersonnelTimeSchedules(newPersonnelTimeSchedules);
        
        // Configurer les observations (prendre celles de la première permanence)
        setObservations(periodPermanences[0].observations || '');
      }
    }
    
    setOpenViewDialog(true);
  };

  const handleOpenEditDialog = (permanence) => {
    setEditMode(true);
    setIsEditingWholePeriod(false); // Désactiver le flag de modification générale (modification individuelle)
    setSelectedPermanenceToEdit(permanence);
    
    // Récupérer la période de la permanence
    const periodId = permanence.period;
    setSelectedPeriod(periodId);
    
    
    // Réinitialiser les sélections avant de les configurer
    setPersonnelSelections(new Map());
    
    // Récupérer les horaires de la permanence
    if (permanence.startTime && permanence.endTime) {
      setGlobalTimeSchedule({
        startTime: permanence.startTime,
        endTime: permanence.endTime
      });
      
      // Initialiser les horaires personnels
      const newPersonnelTimeSchedules = new Map();
      permanence.personnel.forEach(personId => {
        newPersonnelTimeSchedules.set(personId, {
          startTime: permanence.startTime,
          endTime: permanence.endTime
        });
      });
      setPersonnelTimeSchedules(newPersonnelTimeSchedules);
    }
    
    // Trouver la période dans les données
    const selectedVacation = vacationPeriods.find(period => period.id === periodId);
    
    if (selectedVacation) {
      // Configurer les dates de la période
      setSelectedPeriodDates({
        debut: selectedVacation.debut,
        fin: selectedVacation.fin
      });
      
      // Générer les jours de la période
      let debutDate, finDate;
      
      try {
        if (selectedVacation.debut && typeof selectedVacation.debut.toDate === 'function') {
          debutDate = selectedVacation.debut.toDate();
        } else if (selectedVacation.debutDate) {
          debutDate = selectedVacation.debutDate;
        } else {
          debutDate = new Date(selectedVacation.debut);
        }
      } catch (error) {
        console.error('Erreur lors de la conversion de la date de début:', error);
        debutDate = new Date();
      }
      
      try {
        if (selectedVacation.fin && typeof selectedVacation.fin.toDate === 'function') {
          finDate = selectedVacation.fin.toDate();
        } else if (selectedVacation.finDate) {
          finDate = selectedVacation.finDate;
        } else {
          finDate = new Date(selectedVacation.fin);
        }
      } catch (error) {
        console.error('Erreur lors de la conversion de la date de fin:', error);
        finDate = new Date();
      }
      
      if (!isNaN(debutDate.getTime()) && !isNaN(finDate.getTime())) {
        const days = generateDaysBetweenDates(debutDate, finDate);
        setPeriodDays(days);
        
        // Sélectionner le personnel de la permanence
        setSelectedPersonnel(permanence.personnel);
        
        // Configurer les sélections de dates
        
        // Créer une nouvelle Map pour forcer la mise à jour du state
        const newSelections = new Map();
        
        // Traiter chaque membre du personnel
        permanence.personnel.forEach(personId => {
          // Créer un nouveau Set pour stocker les dates normalisées
          const dateSet = new Set();
          
          // S'assurer que days est correctement traité selon son type
          if (Array.isArray(permanence.days)) {
            // Si days est déjà un tableau, normaliser chaque date et l'ajouter au Set
            permanence.days.forEach(day => {
              const normalizedDay = normalizeDateFormat(day);
              if (normalizedDay) {
                dateSet.add(normalizedDay);
              }
            });
          } else if (permanence.days && typeof permanence.days === 'object') {
            try {
              // Si days est un objet, convertir ses valeurs en tableau
              Object.values(permanence.days).forEach(day => {
                const normalizedDay = normalizeDateFormat(day);
                if (normalizedDay) {
                  dateSet.add(normalizedDay);
                }
              });
            } catch (err) {
              console.error('Erreur lors de la conversion des jours en tableau:', err);
            }
          } else if (typeof permanence.days === 'string') {
            // Si days est une chaîne unique
            const normalizedDay = normalizeDateFormat(permanence.days);
            if (normalizedDay) {
              dateSet.add(normalizedDay);
            }
          }
          
          
          // Ajouter le Set de dates au Map des sélections
          newSelections.set(personId, dateSet);
        });
        
        // Mettre à jour l'état avec la nouvelle Map
        setPersonnelSelections(newSelections);
        
        // Configurer les observations
        setObservations(permanence.observations || '');
      }
    }
    
    setOpenViewDialog(true);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  const handleCloseDeleteDialog = () => {
    setDeleteConfirmDialog({
      open: false,
      periodId: null,
      periodName: ''
    });
  };

  const handleDeletePermanence = async (periodId) => {
    try {
      setLoading(true);
      
      // Récupérer toutes les permanences liées à cette période
      const permanencesRef = collection(db, 'permanences');
      const q = query(
        permanencesRef,
        where('establishmentId', '==', userData.establishmentId),
        where('period', '==', periodId)
      );
      const querySnapshot = await getDocs(q);
      
      // Supprimer chaque permanence
      const deletePromises = querySnapshot.docs.map(doc => 
        deleteDoc(firestoreDoc(db, 'permanences', doc.id))
      );
      
      await Promise.all(deletePromises);
      
      // Rafraîchir la liste des permanences
      await fetchPermanences();
      
      setLoading(false);
      showSnackbar(`Les permanences de la période ont été supprimées avec succès`, 'success');
      setDeleteConfirmDialog({ open: false, periodId: null, periodName: '' });
    } catch (error) {
      console.error('Erreur lors de la suppression des permanences:', error);
      setLoading(false);
      showSnackbar(`Erreur lors de la suppression des permanences: ${error.message}`, 'error');
      setDeleteConfirmDialog({ open: false, periodId: null, periodName: '' });
    }
  };

  // Grouper les permanences par période
  const permanencesByPeriod = groupPermanencesByPeriod(permanences);

  return (
    <Box sx={{ p: 3 }}>
      {/* Section des permanences existantes */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h1">
          Gestion des permanences
        </Typography>
        {canCreatePermanences && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenCreateDialog}
          >
            Créer une nouvelle permanence
          </Button>
        )}
      </Box>

      {!canCreatePermanences && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Vous n'avez pas l'autorisation de créer des permanences. Veuillez contacter l'administrateur pour obtenir cette permission.
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Chargement des permanences...</Typography>
        </Box>
      ) : permanences.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="textSecondary">
            Aucune permanence n'a encore été créée.
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {permanencesByPeriod.map(({ periodId, periodName, permanences: periodPermanences }) => (
            <Paper key={periodId} sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6" sx={{ color: 'primary.main' }}>
                    {periodName}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', backgroundColor: '#e8f5e9', px: 2, py: 0.5, borderRadius: 1, border: '1px solid #c8e6c9' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={periodPermanences[0]?.visibleToPersonnel || false}
                          onChange={(e) => {
                            // Mettre à jour l'état local immédiatement pour un feedback visuel instantané
                            const newVisibility = e.target.checked;
                            
                            // Créer une copie profonde des permanences pour mise à jour locale
                            setPermanences(prev => {
                              return prev.map(p => {
                                if (p.period === periodId) {
                                  return { ...p, visibleToPersonnel: newVisibility };
                                }
                                return p;
                              });
                            });
                            
                            // Mettre à jour la visibilité de toutes les permanences de cette période dans Firestore
                            const batch = writeBatch(db);
                            periodPermanences.forEach(permanence => {
                              const permanenceRef = firestoreDoc(db, 'permanences', permanence.id);
                              batch.update(permanenceRef, { visibleToPersonnel: newVisibility });
                            });
                            
                            // Exécuter le batch
                            batch.commit()
                              .then(() => {
                                showSnackbar(`Visibilité des permanences de ${periodName} mise à jour`, 'success');
                              })
                              .catch(error => {
                                console.error("Erreur lors de la mise à jour de la visibilité:", error);
                                showSnackbar("Erreur lors de la mise à jour de la visibilité", 'error');
                                
                                // En cas d'erreur, rétablir l'état précédent
                                setPermanences(prev => {
                                  return prev.map(p => {
                                    if (p.period === periodId) {
                                      return { ...p, visibleToPersonnel: !newVisibility };
                                    }
                                    return p;
                                  });
                                });
                              });
                          }}
                          color="primary"
                          size="small"
                        />
                      }
                      label="Visible pour le personnel"
                    />
                    <Tooltip title="Cochez cette case pour rendre les permanences de cette période visibles dans les comptes personnel">
                      <IconButton size="small">
                        <InfoIcon fontSize="small" color="info" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<PdfIcon />}
                    onClick={() => {
                      setSelectedPeriodForPdf({ 
                        periodName, 
                        permanences: periodPermanences,
                        personnel: personnel,
                        anneeScolaire: establishmentInfo.anneeScolaire,
                        responsableEncadrement: establishmentInfo.responsableEncadrement,
                        gestionnaire: establishmentInfo.gestionnaire,
                        principal: establishmentInfo.principal
                      });
                      setTimeout(handlePrint, 100);
                    }}
                    disabled={loading}
                  >
                    Générer PDF
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => handleOpenEditDialogGeneral(periodPermanences, periodId)}
                    disabled={loading}
                  >
                    Modifier
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => setDeleteConfirmDialog({
                      open: true,
                      periodId: periodId,
                      periodName: periodName
                    })}
                    disabled={loading}
                  >
                    Supprimer
                  </Button>
                </Box>
              </Box>
              <TableContainer sx={{ maxHeight: 440 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ minWidth: 200 }} rowSpan={2}>Nom et Prénom</TableCell>
                      <TableCell sx={{ minWidth: 150 }} rowSpan={2}>Grade</TableCell>
                      <TableCell sx={{ minWidth: 150 }} rowSpan={2}>Service</TableCell>
                      {organizeDataForTable(periodPermanences, personnel).dates.map((date, index) => (
                        <TableCell 
                          key={`day-${date}`}
                          align="center"
                          sx={{ 
                            minWidth: 80,
                            backgroundColor: index % 2 === 0 ? 'background.paper' : 'action.hover',
                            borderBottom: 'none',
                            color: 'primary.main',
                            fontWeight: 'bold'
                          }}
                        >
                          {getShortDayName(new Date(date))}
                        </TableCell>
                      ))}
                    </TableRow>
                    <TableRow>
                      {organizeDataForTable(periodPermanences, personnel).dates.map((date, index) => (
                        <TableCell 
                          key={`date-${date}`}
                          align="center"
                          sx={{ 
                            minWidth: 80,
                            backgroundColor: index % 2 === 0 ? 'background.paper' : 'action.hover'
                          }}
                          title={getDayName(new Date(date))}
                        >
                          {formatShortDate(new Date(date))}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {organizeDataForTable(periodPermanences, personnel).personnel.map((row) => {
                      // Trouver la permanence correspondante à cette personne
                      const personPermanence = periodPermanences.find(p => 
                        p.personnel.includes(row.person.id)
                      );
                      
                      return (
                        <TableRow key={row.person.id}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography 
                                sx={{ 
                                  fontWeight: row.person.role === 'chefService' ? 'bold' : 'normal',
                                  color: row.person.role === 'chefService' ? '#1976d2' : 'inherit'
                                }}
                              >
                                {row.person.lastName} {row.person.firstName}
                                {row.person.role === 'chefService' && (
                                  <Chip 
                                    label="Chef de Service" 
                                    size="small" 
                                    sx={{ 
                                      ml: 1, 
                                      fontSize: '10px', 
                                      height: '20px',
                                      backgroundColor: '#e3f2fd',
                                      color: '#1976d2'
                                    }} 
                                  />
                                )}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {personPermanence?.startTime} - {personPermanence?.endTime}
                              </Typography>
                              {personPermanence && (
                                <Tooltip title="Modifier cette permanence">
                                  <IconButton 
                                    size="small" 
                                    color="primary"
                                    onClick={() => handleOpenEditDialog(personPermanence)}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>{row.person.grade || '-'}</TableCell>
                          <TableCell>{row.person.service || row.person.department || '-'}</TableCell>
                          {row.dates.map((isPresent, index) => (
                            <TableCell 
                              key={index} 
                              align="center"
                              sx={{
                                backgroundColor: index % 2 === 0 ? 'background.paper' : 'action.hover',
                                color: isPresent ? 'success.main' : 'inherit',
                                fontWeight: isPresent ? 'bold' : 'normal'
                              }}
                            >
                              {isPresent ? '✓' : ''}
                            </TableCell>
                          ))}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          ))}
        </Box>
      )}
      
      {/* Dialog pour créer ou modifier une permanence */}
      <Dialog
        open={openViewDialog}
        onClose={() => setOpenViewDialog(false)}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {editMode ? 'Modifier la permanence' : 'Créer une nouvelle permanence'}
            </Typography>
            <IconButton onClick={() => setOpenViewDialog(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold', color: '#1976d2' }}>
              Étape 1: Sélection de la période de vacances
            </Typography>
            <TextField
              select
              fullWidth
              value={selectedPeriod}
              onChange={handlePeriodChange}
            >
              {vacationPeriods.map((period) => (
                <MenuItem key={period.id} value={period.id}>
                  {period.nom}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          {selectedPeriodDates && (
            <>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1, mb: 2 }}>
                Du {formatDate(selectedPeriodDates.debut)} au {formatDate(selectedPeriodDates.fin)}
              </Typography>

              {/* Étape 2: Sélection des jours de permanences */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold', color: '#1976d2' }}>
                  Étape 2: Sélection des jours de permanences
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                  Cochez les dates qui seront des jours de permanence pendant cette période.
                </Typography>
                
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                  {periodDays.map((date) => {
                    // Utiliser normalizeDateFormat pour préserver le jour local
                    const dateStr = normalizeDateFormat(date);
                    const jourFerie = isJourFerie(date);
                    const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                    
                    // Vérifier si cette date est sélectionnée pour au moins un membre du personnel
                    const isDateSelected = Array.from(personnelSelections.values()).some(personDates => {
                      if (personDates instanceof Set) {
                        return Array.from(personDates).some(d => {
                          // Utiliser normalizeDateFormat pour une comparaison cohérente
                          const normalizedDate = normalizeDateFormat(d);
                          return normalizedDate === dateStr;
                        });
                      }
                      return false;
                    });
                    
                    return (
                      <Chip
                        key={dateStr}
                        label={`${getShortDayName(date)} ${formatShortDate(date)}`}
                        onClick={() => {
                          // Sélectionner/désélectionner cette date pour tous les membres du personnel sélectionnés
                          // Utiliser normalizeDateFormat au lieu de toISOString pour préserver le jour local
                          handleColumnSelection(normalizeDateFormat(date));
                        }}
                        clickable
                        variant={isDateSelected ? "filled" : "outlined"}
                        icon={isDateSelected ? <CheckCircleIcon fontSize="small" /> : null}
                        sx={{
                          backgroundColor: isDateSelected 
                            ? '#1976d2' 
                            : (isWeekend ? '#f5f5f5' : (jourFerie ? jourFerie.couleur : '#e0e0e0')),
                          color: isDateSelected ? 'white' : 'inherit',
                          border: isDateSelected ? 'none' : '1px solid #bdbdbd',
                          '&:hover': {
                            backgroundColor: isDateSelected ? '#1565c0' : '#d5d5d5',
                            transform: 'scale(1.05)',
                            transition: 'transform 0.2s',
                          },
                          cursor: 'pointer',
                          fontWeight: isDateSelected ? 'bold' : 'normal',
                          transition: 'all 0.2s',
                        }}
                      />
                    );
                  })}
                </Box>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold', color: '#1976d2' }}>
                  Étape 3: Sélection du personnel et des dates
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                  Sélectionnez le personnel et cochez les dates qui les concernent.
                </Typography>
                
                {/* Barre de recherche pour filtrer le personnel */}
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    label="Rechercher un membre du personnel"
                    placeholder="Nom, prénom, grade, service..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <Box component="span" sx={{ color: 'action.active', mr: 1 }}>
                          🔍
                        </Box>
                      ),
                      endAdornment: searchTerm ? (
                        <IconButton
                          size="small"
                          aria-label="clear search"
                          onClick={() => setSearchTerm('')}
                          edge="end"
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      ) : null
                    }}
                  />
                  <Button 
                    variant="outlined" 
                    color="primary" 
                    size="small"
                    onClick={() => setSearchTerm('')}
                    disabled={!searchTerm}
                  >
                    Réinitialiser
                  </Button>
                </Box>
                
                <TableContainer sx={{ maxHeight: 440, border: '1px solid #e0e0e0', borderRadius: '4px', overflowX: 'auto' }}>

                  <Table stickyHeader size="small" sx={{ minWidth: 650 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            indeterminate={selectedPersonnel.length > 0 && selectedPersonnel.length < personnel.length}
                            checked={personnel.length > 0 && selectedPersonnel.length === personnel.length}
                            onChange={(event) => {
                              if (event.target.checked) {
                                setSelectedPersonnel(personnel.map(p => p.id));
                              } else {
                                setSelectedPersonnel([]);
                                setPersonnelSelections(new Map());
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ minWidth: 50, fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Actions</TableCell>
                        <TableCell sx={{ minWidth: 200, fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Nom et Prénom</TableCell>
                        <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Grade</TableCell>
                        <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Service</TableCell>
                        {periodDays.map((date, index) => {
                          const formattedDate = date.toLocaleDateString('fr-FR', { 
                            weekday: 'short', 
                            day: 'numeric'
                          });
                          
                          const jourFerie = isJourFerie(date);
                          const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                          
                          return (
                            <TableCell 
                              key={date.toISOString()} 
                              align="center"
                              onClick={() => handleColumnSelection(date)}
                              title={jourFerie ? `${jourFerie.nom}` : 'Cliquer pour sélectionner/désélectionner toute la colonne'}
                              sx={{ 
                                backgroundColor: isWeekend ? '#f5f5f5' : (jourFerie ? jourFerie.couleur : 'inherit'),
                                position: 'relative',
                                minWidth: '80px',
                                padding: '8px 4px',
                                cursor: 'pointer',
                                '&:hover': {
                                  backgroundColor: isWeekend ? '#eeeeee' : (jourFerie ? jourFerie.couleur : '#f5f5f5'),
                                  opacity: 0.8
                                },
                                borderBottom: '1px solid #e0e0e0',
                                borderRight: '1px solid #e0e0e0',
                                fontWeight: 'bold'
                              }}
                            >
                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                  {formattedDate}
                                </Typography>
                                {jourFerie && (
                                  <Chip 
                                    label={jourFerie.nom} 
                                    size="small" 
                                    sx={{ 
                                      fontSize: '0.6rem', 
                                      height: '16px',
                                      maxWidth: '70px',
                                      mt: 0.5,
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden'
                                    }} 
                                  />
                                )}
                              </Box>
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {personnel
                        .filter(person => {
                          if (!searchTerm) return true;
                          const searchLower = searchTerm.toLowerCase();
                          return (
                            person.lastName.toLowerCase().includes(searchLower) ||
                            person.firstName.toLowerCase().includes(searchLower) ||
                            (person.grade && person.grade.toLowerCase().includes(searchLower)) ||
                            (person.service && person.service.toLowerCase().includes(searchLower)) ||
                            (person.department && person.department.toLowerCase().includes(searchLower))
                          );
                        })
                        .map((person) => (
                        <TableRow 
                          key={person.id}
                          sx={{
                            backgroundColor: selectedPersonnel.includes(person.id) 
                              ? 'action.selected' 
                              : 'inherit'
                          }}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedPersonnel.includes(person.id)}
                              onChange={() => handlePersonnelSelection(person.id)}
                            />
                          </TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              onClick={() => handleRowSelection(person.id)}
                              disabled={!selectedPersonnel.includes(person.id)}
                            >
                              Tout
                            </Button>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography 
                                sx={{ 
                                  fontWeight: person.role === 'chefService' ? 'bold' : 'normal',
                                  color: person.role === 'chefService' ? '#1976d2' : 'inherit'
                                }}
                              >
                                {person.lastName} {person.firstName}
                              </Typography>
                              {person.role === 'chefService' && (
                                <Chip 
                                  label="Chef de Service" 
                                  size="small" 
                                  sx={{ 
                                    fontSize: '10px', 
                                    height: '20px',
                                    backgroundColor: '#e3f2fd',
                                    color: '#1976d2'
                                  }} 
                                />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>{person.grade || '-'}</TableCell>
                          <TableCell>{person.department || person.service || '-'}</TableCell>
                          {periodDays.map((date, index) => {
                            // Normaliser le format de la date pour la vérification
                            const dateStr = normalizeDateFormat(date);
                            
                            // Vérifier si la date est sélectionnée en utilisant la fonction isDateInSet
                            const personDates = personnelSelections.get(person.id);
                            let isSelected = isDateInSet(dateStr, personDates);
                            
                            // Ajouter un log pour déboguer
                            if (editMode && selectedPermanenceToEdit && person.id === selectedPermanenceToEdit.personnel[0]) {
                            }
                            
                            const jourFerie = isJourFerie(date);
                            const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                            
                            return (
                              <TableCell 
                                key={date.toISOString()} 
                                align="center"
                                sx={{
                                  backgroundColor: isSelected 
                                    ? '#e3f2fd' 
                                    : (isWeekend 
                                      ? '#f5f5f5' 
                                      : (jourFerie 
                                        ? jourFerie.couleur 
                                        : (index % 2 === 0 ? 'background.paper' : 'action.hover'))),
                                  cursor: selectedPersonnel.includes(person.id) ? 'pointer' : 'not-allowed',
                                  borderBottom: '1px solid #e0e0e0',
                                  borderRight: '1px solid #e0e0e0',
                                  padding: '4px',
                                  '&:hover': {
                                    backgroundColor: selectedPersonnel.includes(person.id) ? '#bbdefb' : undefined,
                                  }
                                }}
                                onClick={() => {
                                  if (selectedPersonnel.includes(person.id)) {
                                    // Passer directement la chaîne de date normalisée
                                    handleDateSelection(person.id, dateStr);
                                  }
                                }}
                              >
                                <Checkbox
                                  disabled={!selectedPersonnel.includes(person.id)}
                                  checked={isSelected}
                                  onChange={() => {
                                    // Passer directement la chaîne de date normalisée
                                    handleDateSelection(person.id, dateStr);
                                  }}
                                  size="small"
                                  sx={{
                                    color: isSelected ? '#1976d2' : undefined,
                                    '&.Mui-checked': {
                                      color: '#1976d2',
                                    },
                                    '&.Mui-disabled': {
                                      color: 'rgba(0, 0, 0, 0.26)',
                                    }
                                  }}
                                />
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Horaires
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={useGlobalTimeSchedule}
                      onChange={(e) => setUseGlobalTimeSchedule(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Utiliser des horaires globaux pour tous les personnels"
                  sx={{ mb: 2 }}
                />
                
                {useGlobalTimeSchedule ? (
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <TextField
                      label="Heure de début"
                      type="time"
                      value={globalTimeSchedule.startTime}
                      onChange={(e) => setGlobalTimeSchedule(prev => ({ ...prev, startTime: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ step: 300 }}
                      sx={{ width: '50%' }}
                    />
                    <TextField
                      label="Heure de fin"
                      type="time"
                      value={globalTimeSchedule.endTime}
                      onChange={(e) => setGlobalTimeSchedule(prev => ({ ...prev, endTime: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ step: 300 }}
                      sx={{ width: '50%' }}
                    />
                  </Box>
                ) : (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Horaires par personnel
                    </Typography>
                    {selectedPersonnel.map(personId => {
                      const person = personnel.find(p => p.id === personId);
                      const timeSchedule = personnelTimeSchedules.get(personId) || { startTime: '08:00', endTime: '16:00' };
                      
                      return (
                        <Box key={personId} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Typography variant="body2" sx={{ width: '30%', fontWeight: 'bold' }}>
                            {person?.lastName} {person?.firstName}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 2, width: '70%' }}>
                            <TextField
                              label="Début"
                              type="time"
                              value={timeSchedule.startTime}
                              onChange={(e) => {
                                setPersonnelTimeSchedules(prev => {
                                  const newMap = new Map(prev);
                                  newMap.set(personId, { ...timeSchedule, startTime: e.target.value });
                                  return newMap;
                                });
                              }}
                              InputLabelProps={{ shrink: true }}
                              inputProps={{ step: 300 }}
                              size="small"
                              sx={{ width: '50%' }}
                            />
                            <TextField
                              label="Fin"
                              type="time"
                              value={timeSchedule.endTime}
                              onChange={(e) => {
                                setPersonnelTimeSchedules(prev => {
                                  const newMap = new Map(prev);
                                  newMap.set(personId, { ...timeSchedule, endTime: e.target.value });
                                  return newMap;
                                });
                              }}
                              InputLabelProps={{ shrink: true }}
                              inputProps={{ step: 300 }}
                              size="small"
                              sx={{ width: '50%' }}
                            />
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                )}
                
                <Typography variant="subtitle1" sx={{ mb: 1, mt: 2 }}>
                  Observations
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  value={observations}
                  onChange={(e) => setObservations(e.target.value)}
                  placeholder="Ajouter des observations ou commentaires..."
                />
              </Box>
              
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  onClick={() => setOpenViewDialog(false)}
                  sx={{ mr: 2 }}
                >
                  Annuler
                </Button>
                <Button
                  onClick={handleCreatePermanence}
                  variant="contained"
                  color="primary"
                  disabled={loading || !selectedPeriod || selectedPersonnel.length === 0}
                >
                  {loading ? <CircularProgress size={24} /> : (editMode ? 'Modifier' : 'Créer les permanences')}
                </Button>
              </Box>
            </>
          )}
          {!selectedPeriodDates && (
            <Alert severity="warning">
              Vous n'avez pas l'autorisation de créer des permanences.
            </Alert>
          )}
        </DialogContent>
      </Dialog>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={deleteConfirmDialog.open}
        onClose={handleCloseDeleteDialog}
      >
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <Typography>
            Êtes-vous sûr de vouloir supprimer toutes les permanences de la période <strong>{deleteConfirmDialog.periodName}</strong> ?
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            Attention : Cette action est irréversible.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCloseDeleteDialog}
          >
            Annuler
          </Button>
          <Button 
            variant="contained" 
            color="error"
            onClick={() => handleDeletePermanence(deleteConfirmDialog.periodId)}
          >
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Composant caché pour le PDF - positionné hors écran mais visible pour html2canvas */}
      <div style={{ position: 'absolute', left: '-9999px', top: 0 }} id="pdf-content-container">
        {selectedPeriodForPdf && (
          <PdfContent
            ref={pdfComponentRef}
            periodName={selectedPeriodForPdf.periodName}
            permanences={selectedPeriodForPdf.permanences}
            personnel={selectedPeriodForPdf.personnel}
            establishmentName={establishmentInfo.name}
            anneeScolaire={selectedPeriodForPdf.anneeScolaire}
            responsableEncadrement={selectedPeriodForPdf.responsableEncadrement}
            gestionnaire={selectedPeriodForPdf.gestionnaire}
            principal={selectedPeriodForPdf.principal}
            logo={establishmentInfo?.logo}
          />
        )}
      </div>
    </Box>
  );
};

export default ScheduleManager;
